import { createBrowserRouter, Navigate } from 'react-router-dom';
import { RequireRole, RoleHomeRedirect } from './guards';

import { LearnerLayout } from '@/layouts/LearnerLayout';
import { InstructorLayout } from '@/layouts/InstructorLayout';
import { AdminLayout } from '@/layouts/AdminLayout';

import { Welcome } from '@/features/onboarding/Welcome';
import { LineCallback } from '@/features/onboarding/LineCallback';
import { VerifyPage } from '@/features/certificate/VerifyPage';
import { NotFound } from '@/features/NotFound';

import { LearnerHome } from '@/features/learner/LearnerHome';
import { LearnerCatalog } from '@/features/learner/LearnerCatalog';
import { LearnerCourseDetail } from '@/features/learner/LearnerCourseDetail';
import { LearnerLearning } from '@/features/learner/LearnerLearning';
import { LearnerWallet } from '@/features/learner/LearnerWallet';
import { LearnerCertView } from '@/features/learner/LearnerCertView';
import { LearnerProfile } from '@/features/learner/LearnerProfile';
import { LearnerEnroll } from '@/features/learner/flows/LearnerEnroll';
import { LearnerLesson } from '@/features/learner/flows/LearnerLesson';
import { LearnerTest } from '@/features/learner/flows/LearnerTest';
import { LearnerEval } from '@/features/learner/flows/LearnerEval';
import { LearnerAttendance } from '@/features/learner/flows/LearnerAttendance';
import { LearnerReview } from '@/features/learner/flows/LearnerReview';

import { InstructorDashboard } from '@/features/instructor/InstructorDashboard';
import { InstructorCourses } from '@/features/instructor/InstructorCourses';
import { InstructorTests } from '@/features/instructor/InstructorTests';
import { InstructorAttendance } from '@/features/instructor/InstructorAttendance';
import { InstructorAnalytics } from '@/features/instructor/InstructorAnalytics';
import { InstructorCertPreview } from '@/features/instructor/InstructorCertPreview';
import { InstructorLearners } from '@/features/instructor/InstructorLearners';

import { AdminDashboard } from '@/features/admin/AdminDashboard';
import { AdminInvites } from '@/features/admin/AdminInvites';
import { AdminUsers } from '@/features/admin/AdminUsers';
import { AdminOrg } from '@/features/admin/AdminOrg';
import { AdminVerify } from '@/features/admin/AdminVerify';
import { AdminCourses } from '@/features/admin/AdminCourses';
import { AdminReports } from '@/features/admin/AdminReports';
import { AdminAppearance } from '@/features/admin/AdminAppearance';

export const router = createBrowserRouter([
  { path: '/', element: <RoleHomeRedirect /> },
  { path: '/welcome', element: <Welcome /> },
  { path: '/auth/line/callback', element: <LineCallback /> },
  { path: '/verify/:id', element: <VerifyPage /> },

  // ── Learner (staff and above) ──
  {
    path: '/learn',
    element: (
      <RequireRole min="staff">
        <LearnerLayout />
      </RequireRole>
    ),
    children: [
      { index: true, element: <LearnerHome /> },
      { path: 'catalog', element: <LearnerCatalog /> },
      { path: 'catalog/:id', element: <LearnerCourseDetail /> },
      { path: 'catalog/:id/enroll', element: <LearnerEnroll /> },
      { path: 'learning', element: <LearnerLearning /> },
      // Enrolled course detail in the LEARNING context, so the bottom nav stays
      // on "Learning" (the /catalog/:id route keeps the nav on "Catalog").
      { path: 'learning/:id', element: <LearnerCourseDetail /> },
      { path: 'learning/:id/lesson/:idx', element: <LearnerLesson /> },
      { path: 'course/:id/test/:mode', element: <LearnerTest /> },
      { path: 'course/:id/eval', element: <LearnerEval /> },
      { path: 'course/:id/attendance', element: <LearnerAttendance /> },
      { path: 'course/:id/review', element: <LearnerReview /> },
      { path: 'wallet', element: <LearnerWallet /> },
      { path: 'wallet/:certId', element: <LearnerCertView /> },
      { path: 'profile', element: <LearnerProfile /> },
    ],
  },

  // ── Instructor ──
  {
    path: '/teach',
    element: (
      <RequireRole min="instructor">
        <InstructorLayout />
      </RequireRole>
    ),
    children: [
      { index: true, element: <InstructorDashboard /> },
      { path: 'courses', element: <InstructorCourses /> },
      { path: 'learners', element: <InstructorLearners /> },
      // Editing surfaces are disabled (view-only console) — redirect to dashboard.
      { path: 'editor', element: <Navigate to="/teach" replace /> },
      { path: 'tests', element: <InstructorTests /> },
      { path: 'attendance', element: <InstructorAttendance /> },
      { path: 'analytics', element: <InstructorAnalytics /> },
      { path: 'cert-preview', element: <InstructorCertPreview /> },
      { path: 'certs', element: <Navigate to="/teach" replace /> },
    ],
  },

  // ── Admin / Super Admin ──
  {
    path: '/admin',
    element: (
      <RequireRole min="admin">
        <AdminLayout />
      </RequireRole>
    ),
    children: [
      // Non-super admins are scoped to invite generation only; everything else
      // under /admin is superadmin-gated.
      { index: true, element: <RequireRole min="superadmin"><AdminDashboard /></RequireRole> },
      { path: 'reports', element: <RequireRole min="superadmin"><AdminReports /></RequireRole> },
      { path: 'invites', element: <AdminInvites /> },
      { path: 'users', element: <RequireRole min="superadmin"><AdminUsers /></RequireRole> },
      { path: 'org', element: <RequireRole min="superadmin"><AdminOrg /></RequireRole> },
      { path: 'courses', element: <RequireRole min="superadmin"><AdminCourses /></RequireRole> },
      { path: 'verify', element: <RequireRole min="superadmin"><AdminVerify /></RequireRole> },
      { path: 'appearance', element: <RequireRole min="superadmin"><AdminAppearance /></RequireRole> },
    ],
  },

  { path: '*', element: <NotFound /> },
]);
